import { useBlockPanelPropsContext } from "../../lib/panel/panel-container";
import PanelContent from "../../lib/panel/panel-content";
import { cn } from "@babackapp/ui/lib/utils";
import { useCallback } from "react";
import { ButtonBlock, ButtonVariant } from "./types";
import { DoubleButtonsBlock } from "../double-buttons/types";
import { getThemeButtonStyle } from "./shared-button-styling";
import { useEmailThemeRadius } from "../../hooks/use-emails-theme-radius";
import useThemeContextualColors, { sectionToParent } from "../../hooks/use-theme-contextual-colors";
import { useTranslation } from "react-i18next";


interface EmailPanelStyleProps {
	variantFieldName?: string;
}


const VARIANT_OPTIONS: Array<{
	value: ButtonVariant;
	label: string;
	description: string;
}> = [
	{
		value: "default",
		label: "editorBlocksLibrary.blocks.button.variant.default.title",
		description: "editorBlocksLibrary.blocks.button.variant.default.description",
	},
	{
		value: "primary",
		label: "editorBlocksLibrary.blocks.button.variant.primary.title",
		description: "editorBlocksLibrary.blocks.button.variant.primary.description",
	},
	{
		value: "secondary",
		label: "editorBlocksLibrary.blocks.button.variant.secondary.title",
		description: "editorBlocksLibrary.blocks.button.variant.secondary.description",
	},
	{
		value: "outline",
		label: "editorBlocksLibrary.blocks.button.variant.outline.title",
		description: "editorBlocksLibrary.blocks.button.variant.outline.description",
	},
];


function SettingsCard({ children, className }: {
	children: React.ReactNode;
	className?: string;
}) {
	return (
		<div
			className={cn(
				"bg-muted/10 rounded-xl p-0 border border-border/50 flex flex-row overflow-hidden",
				className,
			)}
		>
			{children}
		</div>
	);
}


export function EmailPanelStyle({ variantFieldName = "variant" }: EmailPanelStyleProps = {}) {
	const blockProps = useBlockPanelPropsContext();
	const { formAPI, theme, section } = blockProps;
	const block = blockProps.block as ButtonBlock | DoubleButtonsBlock;

	const variant = (block.properties as Record<string, ButtonVariant>)?.[variantFieldName] ?? "default";
	const buttonRadius = useEmailThemeRadius(theme, "button");
  const { t } = useTranslation("editor-blocks-library");
	const colors = useThemeContextualColors(theme, sectionToParent(section));

	const handleVariantChange = useCallback((newVariant: ButtonVariant) => {
		formAPI.updateField(`${formAPI.blockPath}.properties.${variantFieldName}`, newVariant);
	}, [formAPI, variantFieldName]);

	// Render button preview for each variant
	const renderButtonPreview = (variantOption: ButtonVariant) => {
		const previewStyles = Object.assign(getThemeButtonStyle({
			theme,
			variant: variantOption,
			textColor: colors.text,
		}), {
			fontSize: "0.6rem",
			padding: "3px 6px",
		});

		return (
			<div
				className={cn(
					"p-3 h-full flex items-center justify-center shadow-[inset_6px_0_8px_-4px_rgba(0,0,0,0.1)]",
				)}
				style={{ borderColor: colors.contrast, backgroundColor: colors.bg }}
			>
				<div style={previewStyles} className={cn(buttonRadius)}>Aperçu</div>
			</div>
		);
	};

	return (
		<PanelContent title="Style">
			<div className="grid grid-cols-1 gap-3 w-full">
				{VARIANT_OPTIONS.map((option) => (
					<SettingsCard
						key={option.value}
						className="cursor-pointer"
					>
						<label className="flex items-center space-x-3 cursor-pointer w-full">
							<div className="flex items-center space-x-3 cursor-pointer w-full p-3">
								<input
									type="radio"
									name={variantFieldName}
									value={option.value}
									checked={variant === option.value}
									onChange={() => handleVariantChange(option.value)}
									className="sr-only"
								/>
								<div
									className={cn(
										"w-4 h-4 rounded-full border-2 flex items-center justify-center",
										variant === option.value
											? "border-primary bg-primary"
											: "border-muted-foreground",
									)}
								>
									{variant === option.value && (
										<div className="w-2 h-2 rounded-full bg-primary-foreground" />
									)}
								</div>
								<div className="flex-1">
									<div className="font-medium text-xs">
										{t(option.label)}
									</div>
								</div>
							</div>
							<div className="ml-auto h-full">
								{renderButtonPreview(option.value)}
							</div>
						</label>
					</SettingsCard>
				))}
			</div>
		</PanelContent>
	);
}
